import React from 'react';
import { 
  Form, 
  InputNumber, 
  Select, 
  Slider, 
  Switch, 
  Button, 
  Row, 
  Col,
  Tooltip
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ImageProcessOptions } from '../utils/imageProcessor';

const { Option } = Select;

interface ImageProcessorFormProps {
  onProcess: (options: ImageProcessOptions) => void;
  loading: boolean;
  originalWidth?: number;
  originalHeight?: number;
}

const ImageProcessorForm: React.FC<ImageProcessorFormProps> = ({
  onProcess,
  loading,
  originalWidth,
  originalHeight
}) => {
  const [form] = Form.useForm();
  
  const handleFinish = (values: any) => {
    onProcess({
      maxWidth: values.width,
      maxHeight: values.height,
      quality: values.quality / 100,
      format: values.format,
      maintainAspectRatio: values.maintainAspectRatio
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        width: originalWidth || 1280,
        height: originalHeight || 720,
        quality: 80,
        format: 'jpeg',
        maintainAspectRatio: true
      }}
    >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={
              <span>
                目标宽度 (像素)
                <Tooltip title="设置图片的目标宽度">
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </span>
            }
            name="width"
            rules={[{ required: true, message: '请输入宽度' }]}
          >
            <InputNumber min={1} max={5000} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={
              <span>
                目标高度 (像素)
                <Tooltip title="设置图片的目标高度">
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </span>
            }
            name="height"
            rules={[{ required: true, message: '请输入高度' }]}
          >
            <InputNumber min={1} max={5000} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label={
          <span>
            保持宽高比
            <Tooltip title="开启后将保持原始图片的宽高比，可能导致实际尺寸小于设定值">
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </span>
        }
        name="maintainAspectRatio"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label={
          <span>
            图片质量
            <Tooltip title="较低的质量可以减小文件大小，但可能降低图片清晰度">
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </span>
        }
        name="quality"
      >
        <Slider
          marks={{
            10: '低',
            50: '中',
            80: '高',
            100: '最高'
          }}
          min={10}
          max={100}
        />
      </Form.Item>

      <Form.Item
        label={
          <span>
            输出格式
            <Tooltip title="不同格式适用于不同场景：JPEG适合照片，PNG支持透明背景，WebP提供更好的压缩率">
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </span>
        }
        name="format"
      >
        <Select>
          <Option value="jpeg">JPEG</Option>
          <Option value="png">PNG</Option>
          <Option value="webp">WebP</Option>
        </Select>
      </Form.Item>

      <Form.Item>
        <Button 
          type="primary" 
          htmlType="submit" 
          loading={loading}
          block
        >
          处理图片
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ImageProcessorForm;